import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BenchmarkCategoryRow { label: string; count: number; avg_dev_pct: number }
export interface BenchmarkTopRow { title: string; price_micros: number; benchmark_price_micros: number; dev_pct: number }
export interface PriceBenchmarkSummary {
  product_total: number;
  bench_count: number;
  above_count: number;
  within_count: number;
  below_count: number;
  median_dev_pct: number | null;
  p90_dev_pct: number | null;
  below_avg_dev_pct: number | null;
  currency: string | null;
  pulled_at: string | null;
  categories: BenchmarkCategoryRow[];
  top_above: BenchmarkTopRow[];
  /** Band -5..+5 %, DESC (knapp unter der +5-%-Grenze zuerst). */
  top_within: BenchmarkTopRow[];
  /** Unter -5 %, ASC (staerkste Unterbietung zuerst). */
  top_below: BenchmarkTopRow[];
}

/**
 * Report V2 Kapitel 2 (Spec §3.5): serverseitig verdichtete Preis-Benchmarks.
 * `data === null` heisst "kein Kapitel rendern": entweder hat der Feed keine
 * Benchmark-Zeilen (bench_count 0), oder die RPC hat NULL geliefert (fremder
 * Feed, bewusst ununterscheidbar von "existiert nicht").
 */
export function usePriceBenchmarkSummary(feedId: string | null) {
  return useQuery({
    queryKey: ['price-benchmark-summary', feedId],
    enabled: !!feedId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PriceBenchmarkSummary | null> => {
      const { data, error } = await supabase.rpc('price_benchmark_summary', { p_feed_id: feedId });
      if (error) throw error;
      if (!data || typeof data !== 'object') return null;
      const payload = data as unknown as PriceBenchmarkSummary;
      if (!payload.bench_count) return null;
      // Die RPC waechst additiv (v3 hat top_within/top_below ergaenzt). Laeuft
      // ein Client gegen eine aeltere Fassung, fehlen die Keys im Payload und
      // jeder Konsument liefe auf undefined.map. Hier normalisiert, damit die
      // Komponenten sich auf Arrays verlassen duerfen.
      return {
        ...payload,
        categories: payload.categories ?? [],
        top_above: payload.top_above ?? [],
        top_within: payload.top_within ?? [],
        top_below: payload.top_below ?? [],
      };
    },
  });
}
